// EstimateCard (job page round 2, Amir 26 Oct: "show the estimates as estimate cards: draft, presented,
// selected or approved, sold, declined and expired"): one estimate as the office sees it, a document on
// the sheet. Its number and name, its state in a word, its options one a line (letter, what, price;
// the recommended one says so; the chosen or sold one drawn heavier and saying so), then one line on
// where it stands (who presented it, how it was sent, when it was seen, signed, declined, expires).
// The state is drawn by the frame, never a colour: a draft dashed, presented and chosen a hairline,
// sold the heavy ink line, declined and expired a hairline in muted ink. Red is only for a problem,
// one line on any card before it's sold (waiting for the office's OK on the margin or a price, below
// the floor, a sale waiting past its day). "Presented" covers presented and sent; "Chosen" is derived:
// an option chosen, nothing signed. The name opens the estimate. Its
// links sit at the foot. Reads only --estcard-*.
import type { ReactNode } from 'react';
import './EstimateCard.css';

export type EstimateState = 'draft' | 'presented' | 'chosen' | 'sold' | 'declined' | 'expired';

export const ESTIMATE_WORDS: Record<EstimateState, string> = {
  draft: 'Draft',
  presented: 'Presented',
  chosen: 'Chosen, not signed',
  sold: 'Sold',
  declined: 'Declined',
  expired: 'Expired',
};

export interface EstimateCardOption {
  id: string;
  /** "A" */
  letter: string;
  /** "Good", "Better", "Best", when it has a tier. */
  tier?: string;
  title: string;
  /** "$3,640.00" */
  price: string;
  isRecommended?: boolean;
  /** The customer chose it, or it was sold. */
  isChosen?: boolean;
}

export interface EstimateCardProps {
  /** "Estimate 3108" */
  number: string;
  title: string;
  state: EstimateState;
  /** In place of the state's own word ("Sold for later"). */
  word?: string;
  options: EstimateCardOption[];
  /** Where it stands, in one line: "Presented Wed Oct 21 by Sam · sent by text · seen Thu Oct 22". */
  line: ReactNode;
  /** One problem, in red. */
  problem?: ReactNode;
  /** Links at its foot. */
  actions?: ReactNode;
  /** The name opens the estimate. */
  onOpen?: () => void;
}

export function EstimateCard({
  number,
  title,
  state,
  word,
  options,
  line,
  problem,
  actions,
  onOpen,
}: EstimateCardProps) {
  const name = (
    <>
      <span className="fl-estcard__num">{number}</span>
      <span className="fl-estcard__title">{title}</span>
    </>
  );
  const said = word ?? ESTIMATE_WORDS[state];
  const chosenWord = state === 'sold' ? 'sold' : 'chosen';
  return (
    <article className="fl-estcard" data-state={state} aria-label={`${number}, ${title}: ${said}`}>
      <div className="fl-estcard__head">
        <h3 className="fl-estcard__name">
          {onOpen ? (
            <button type="button" className="fl-estcard__open" aria-haspopup="dialog" onClick={onOpen}>
              {name}
            </button>
          ) : (
            name
          )}
        </h3>
        <span className="fl-estcard__word">{said}</span>
      </div>
      <ul className="fl-estcard__opts" aria-label={`${number}’s options`}>
        {options.map((o) => (
          <li key={o.id} className="fl-estcard__opt" data-chosen={o.isChosen ? '' : undefined}>
            <span className="fl-estcard__letter">{o.letter}</span>
            <span className="fl-estcard__what">
              {o.title}
              {o.tier ? <span className="fl-estcard__tier"> · {o.tier}</span> : null}
              {o.isRecommended ? <span className="fl-estcard__tier"> · recommended</span> : null}
              {o.isChosen ? <b className="fl-estcard__chosen"> · {chosenWord}</b> : null}
            </span>
            <span className="fl-estcard__price">{o.price}</span>
          </li>
        ))}
      </ul>
      <p className="fl-estcard__line">{line}</p>
      {problem ? <p className="fl-estcard__bad">{problem}</p> : null}
      {actions ? <div className="fl-estcard__acts">{actions}</div> : null}
    </article>
  );
}

/** Several cards, one under another. */
/** The job's estimates, newest first, one under another; named for screen readers. */
export function EstimateCards({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="fl-estcard-list" role="group" aria-label={label}>
      {children}
    </div>
  );
}
